import { useState } from "react";
import {
  Avatar,
  Button,
  Divider,
  Drawer,
  Input,
  List,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { CheckOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import SubtaskFormModal from "./SubtaskFormModal";
import { useAuth } from "../../lib/auth";
import type { TaskItem, SubtaskItem, TaskStatus } from "../../lib/types";
import type { ProjectItem, UserOption } from "./types";

interface TaskDrawerProps {
  task: TaskItem | null;
  project: ProjectItem;
  subtasks: SubtaskItem[];
  users: UserOption[];
  taskComment: string;
  setTaskComment: (value: string) => void;
  onClose: () => void;
  onSelectSubtask: (subtask: SubtaskItem) => void;
  onUpdateSubtaskStatus: (
    subtask: SubtaskItem,
    status: TaskStatus,
  ) => Promise<void> | void;
  onEditSubtask: (subtask: SubtaskItem) => void;
  onDeleteSubtask: (subtask: SubtaskItem) => void | Promise<void>;
  onSubmitTaskComment: () => Promise<void> | void;
}

const isOverdue = (dueDate?: string | null, status?: TaskStatus) => {
  if (!dueDate) return false;
  if (status === "done") return false;
  return dayjs(dueDate).isBefore(dayjs(), "day");
};

export default function TaskDrawer({
  task,
  project: _project,
  subtasks,
  users,
  taskComment,
  setTaskComment,
  onClose,
  onSelectSubtask,
  onUpdateSubtaskStatus,
  onEditSubtask,
  onDeleteSubtask,
  onSubmitTaskComment,
}: TaskDrawerProps) {
  const [subtaskModalOpen, setSubtaskModalOpen] = useState(false);
  const { isAdmin, canWrite } = useAuth();
  if (!task) return null;
  const taskSubtasks = subtasks.filter((s) => s.taskId === task.id);

  return (
    <>
    <Drawer
      title={task.title || "Task Details"}
      open={Boolean(task)}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <Tabs
        items={[
          {
            key: "details",
            label: "Details",
            children: (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div>
                  <Typography.Text type="secondary">Description</Typography.Text>
                  <Typography.Paragraph>
                    {task.description || "No description provided."}
                  </Typography.Paragraph>
                </div>
                <div>
                  <Typography.Text type="secondary">Timeline</Typography.Text>
                  <Typography.Paragraph>
                    Start: {task.startDate || "—"}
                    <br />
                    End: {task.endDate || "—"}
                  </Typography.Paragraph>
                </div>
                {task.dependencies && (
                  <div>
                    <Typography.Text type="secondary">Dependencies</Typography.Text>
                    <Typography.Paragraph>{task.dependencies}</Typography.Paragraph>
                  </div>
                )}
                {task.completedAt && (
                  <div>
                    <Typography.Text type="secondary">Completed</Typography.Text>
                    <Typography.Paragraph>
                      {new Date(task.completedAt).toLocaleString()}
                    </Typography.Paragraph>
                  </div>
                )}
              </Space>
            ),
          },
          {
            key: "subtasks",
            label: `Subtasks (${taskSubtasks.length})`,
            children: (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {canWrite && (
                  <Button
                    type="primary"
                    onClick={() => setSubtaskModalOpen(true)}
                    aria-label="Add new subtask to task"
                  >
                    New Subtask
                  </Button>
                )}
                {taskSubtasks.length === 0 ? (
                  <Typography.Text type="secondary">
                    No subtasks yet for this task.
                  </Typography.Text>
                ) : (
                  <List
                    aria-label={`Subtasks for task ${task.title}`}
                    dataSource={taskSubtasks}
                    renderItem={(subtask) => (
                      <List.Item
                        style={{ cursor: "pointer" }}
                        onClick={() => onSelectSubtask(subtask)}
                        actions={[
                          ...(canWrite
                            ? [
                                <Tooltip key="complete" title="Mark complete">
                                  <Button
                                    type="text"
                                    icon={<CheckOutlined />}
                                    aria-label={`Mark subtask ${subtask.title} complete`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onUpdateSubtaskStatus(subtask, "done");
                                    }}
                                    disabled={subtask.status === "done"}
                                  />
                                </Tooltip>,
                                <Tooltip key="edit" title="Edit subtask">
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    aria-label={`Edit subtask ${subtask.title}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditSubtask(subtask);
                                    }}
                                  />
                                </Tooltip>,
                              ]
                            : []),
                          ...(isAdmin
                            ? [
                                <Tooltip key="delete" title="Delete subtask">
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    aria-label={`Delete subtask ${subtask.title}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteSubtask(subtask);
                                    }}
                                  />
                                </Tooltip>,
                              ]
                            : []),
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Space>
                              <Typography.Text>{subtask.title}</Typography.Text>
                              <Tag
                                color={subtask.status === "done" ? "success" : "default"}
                              >
                                {subtask.status.replace("_", " ")}
                              </Tag>
                              {subtask.dueDate &&
                                isOverdue(subtask.dueDate, subtask.status) && (
                                  <Tag color="error">Overdue</Tag>
                                )}
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={0}>
                              <Typography.Text type="secondary">
                                Due {subtask.dueDate || "—"} · Assigned to{" "}
                                {users.find((u) => u.uid === subtask.assignedTo)
                                  ?.displayName ||
                                  users.find((u) => u.uid === subtask.assignedTo)?.email ||
                                  "Unassigned"}
                              </Typography.Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Space>
            ),
          },
          {
            key: "comments",
            label: `Comments (${(task.comments || []).length})`,
            children: (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {(task.comments || []).length === 0 ? (
                  <Typography.Text type="secondary">No comments yet.</Typography.Text>
                ) : (
                  <List
                    aria-label="Task comments"
                    dataSource={task.comments || []}
                    renderItem={(comment) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar>{comment.authorName?.[0] || "U"}</Avatar>}
                          title={
                            <Space direction="vertical" size={0}>
                              <Typography.Text strong>
                                {comment.authorName || "User"}
                              </Typography.Text>
                              {comment.createdAt && (
                                <Typography.Text type="secondary" style={{ fontSize: "11px" }}>
                                  {new Date(comment.createdAt).toLocaleString()}
                                </Typography.Text>
                              )}
                            </Space>
                          }
                          description={<Typography.Text>{comment.message}</Typography.Text>}
                        />
                      </List.Item>
                    )}
                  />
                )}
                <Divider />
                <Input.TextArea
                  aria-label="Add new comment"
                  value={taskComment}
                  onChange={(event) => setTaskComment(event.target.value)}
                  placeholder="Add a comment..."
                  autoSize={{ minRows: 3 }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                      onSubmitTaskComment();
                    }
                  }}
                />
                <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
                  <Typography.Text type="secondary">Cmd+Enter to send</Typography.Text>
                  <Button
                    type="primary"
                    onClick={onSubmitTaskComment}
                    disabled={!taskComment.trim()}
                  >
                    Send
                  </Button>
                </Space>
              </Space>
            ),
          },
        ]}
      />
    </Drawer>
    <SubtaskFormModal
      open={subtaskModalOpen}
      parentTask={task}
      users={users}
      onCancel={() => setSubtaskModalOpen(false)}
    />
    </>
  );
}
